import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Instagram, GraduationCap, Wine, Beer, GlassWater, Droplet } from "lucide-react";
import InstagramGrid from "@/components/InstagramGrid";
import CocktailOfTheWeek from "@/components/CocktailOfTheWeek";

const categories = [
  { title: "Cocktails", desc: "Classic recipes, modern twists", to: "/cocktails", img: "https://images.unsplash.com/photo-1470337458703-46ad1756a187?auto=format&fit=crop&w=900&q=80", icon: GlassWater },
  { title: "Whisky", desc: "From Scotland to Bengaluru", to: "/whisky", img: "https://images.pexels.com/photos/2198400/pexels-photo-2198400.jpeg?auto=compress&cs=tinysrgb&w=900", icon: Sparkles },
  { title: "Wine", desc: "The pour, the pairing, the vibe", to: "/wine", img: "https://images.unsplash.com/photo-1456427651609-f494c36fbc45?auto=format&fit=crop&w=900&q=80", icon: Wine },
  { title: "Beer", desc: "Lagers, ales & everything in between", to: "/beer", img: "https://images.unsplash.com/photo-1608270586620-248524c67de9?auto=format&fit=crop&w=900&q=80", icon: Beer },
  { title: "Rum", desc: "Old Monk to Havana Club", to: "/rum", img: "https://images.unsplash.com/photo-1541546006121-5c3bc5e8c7b9?auto=format&fit=crop&w=900&q=80", icon: Droplet },
];

const learnPreview = [
  { title: "Understanding ABV", read: "3 min", slug: "understanding-abv" },
  { title: "Liquor vs Liqueur", read: "2 min", slug: "liquor-vs-liqueur" },
  { title: "Glassware, Explained", read: "4 min", slug: "understanding-glassware" },
  { title: "Cocktail Basics", read: "5 min", slug: "cocktail-basics" },
];

export default function Home() {
  return (
    <div className="bg-ink">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16 pb-14 sm:pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <p className="eyebrow">A Sip Alchemy Original · Est. Mumbai</p>
            <h1 className="mt-4 font-serif-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight text-cream">
              What should<br />you drink<br />
              <span className="italic text-pink-brand font-serif-editor font-medium">tonight?</span>
            </h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-cream/70 leading-relaxed font-body">
              A beginner-friendly journey into cocktails, whisky, wine and beer — designed for the curious Indian palate. Take our quiz, meet your match, sip smarter.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link to="/drink-finder" className="btn-pill btn-pink" data-testid="hero-find-my-drink">
                Find My Drink <ArrowRight size={18} />
              </Link>
              <Link to="/cocktails" className="btn-pill btn-outline-ink" data-testid="hero-explore-cocktails">
                Explore Cocktails
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-4 text-sm text-cream/60 font-body">
              <div><span className="font-serif-display text-2xl text-cream block leading-none">20+</span>Curated cocktails</div>
              <div className="hidden sm:block w-px h-8 bg-cream/15" />
              <div><span className="font-serif-display text-2xl text-cream block leading-none">7</span>Learn modules</div>
              <div className="hidden sm:block w-px h-8 bg-cream/15" />
              <div><span className="font-serif-display text-2xl text-cream block leading-none">Weekly</span>Editor's pick</div>
            </div>
          </div>

          <div className="lg:col-span-5 relative animate-fade-up delay-200">
            <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1500217052183-bc01eee1a74e?auto=format&fit=crop&w=1200&q=85"
                alt="Cocktail"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-ink/10 to-transparent" />
              <div className="absolute left-5 bottom-5 right-5 text-cream">
                <p className="eyebrow" style={{ color: "#F9C0D2" }}>Find your match</p>
                <p className="font-serif-display text-2xl sm:text-3xl font-bold mt-1">Espresso Martini</p>
                <p className="text-xs sm:text-sm text-cream/80 mt-1">Bold · Party-ready · 96% Match</p>
              </div>
            </div>
            <div className="absolute -right-3 -top-3 sm:-right-4 sm:-top-4 bg-pink text-cream rounded-full h-20 w-20 sm:h-24 sm:w-24 flex items-center justify-center text-center font-serif-editor italic text-xs sm:text-sm rotate-[8deg] shadow-lg pointer-events-none z-10">
              new<br />drops<br />weekly
            </div>
          </div>
        </div>
      </section>

      {/* COCKTAIL OF THE WEEK */}
      <CocktailOfTheWeek />

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="flex items-end justify-between mb-8 sm:mb-10">
          <div>
            <p className="eyebrow">The Cellar</p>
            <h2 className="mt-2 font-serif-display text-3xl sm:text-4xl md:text-5xl font-bold text-cream">Pick your poison, gently.</h2>
          </div>
          <Link to="/learn" className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-cream hover:text-pink-brand">
            All categories <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
          {categories.map(c => (
            <Link
              to={c.to}
              key={c.title}
              className="group relative rounded-2xl overflow-hidden aspect-[3/4] card-cream p-0"
              data-testid={`category-${c.title.toLowerCase()}`}
            >
              <img src={c.img} alt={c.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/30 to-transparent" />
              <div className="absolute left-4 right-4 bottom-4 text-cream">
                <c.icon size={20} className="mb-2 opacity-80" />
                <p className="font-serif-display text-xl sm:text-2xl font-bold leading-tight">{c.title}</p>
                <p className="text-xs sm:text-sm text-cream/75 mt-1">{c.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* LEARN preview */}
      <section className="bg-ink-panel py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-4">
              <p className="eyebrow">Sip Alchemy · 101</p>
              <h2 className="mt-2 font-serif-display text-3xl sm:text-4xl md:text-5xl font-bold text-cream leading-tight">
                Actually learn about<br /><span className="italic font-serif-editor text-pink-brand">what's in your glass.</span>
              </h2>
              <p className="mt-5 text-cream/70 font-body max-w-md">
                No jargon, no snobbery. Just the essentials — served the way a friend who knows about drinks would explain them.
              </p>
              <Link to="/learn" className="btn-pill btn-ink mt-6" data-testid="learn-cta">
                Start Learning <GraduationCap size={18} />
              </Link>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
              {learnPreview.map((l, i) => (
                <Link
                  to={`/learn/${l.slug}`}
                  key={l.slug}
                  className="card-cream p-6 flex flex-col justify-between min-h-[180px]"
                  data-testid={`learn-preview-${l.slug}`}
                >
                  <span className="chip chip-pink w-fit">#{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <p className="font-serif-display text-2xl font-bold text-cream mt-6">{l.title}</p>
                    <p className="text-sm text-cream/60 mt-2 flex items-center justify-between">
                      <span>{l.read} read</span>
                      <ArrowRight size={16} />
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Bar Cart Teaser */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="card-dark relative overflow-hidden rounded-[2rem] p-8 sm:p-14">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <p className="eyebrow">The Starter Bar Cart</p>
              <h2 className="mt-2 font-serif-display text-3xl sm:text-4xl md:text-5xl font-bold text-cream leading-tight">
                Five bottles.<br /><span className="italic font-serif-editor text-pink-brand">Endless nights in.</span>
              </h2>
              <p className="mt-4 text-cream/70 font-body max-w-xl">
                The shortest possible shopping list for the most possible drinks — ranked by how many cocktails each bottle unlocks in our database.
              </p>
              <Link to="/bar-cart" className="btn-pill btn-pink mt-6" data-testid="home-bar-cart-cta">
                See the five bottles <ArrowRight size={18} />
              </Link>
            </div>
            <div className="lg:col-span-5">
              <div className="grid grid-cols-5 gap-2">
                {["Gin", "Whisky", "Rum", "Vodka", "Tequila"].map((s, i) => (
                  <div key={s} className="aspect-[1/2.2] rounded-full bg-ink-2 border border-cream/10 flex items-end pb-4 justify-center">
                    <span className="font-serif-editor italic text-xs sm:text-sm text-cream/85 -rotate-90 whitespace-nowrap">{i + 1}. {s}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="absolute -right-24 -top-24 h-64 w-64 rounded-full bg-pink/20 blur-3xl pointer-events-none" />
        </div>
      </section>

      {/* Instagram feed */}
      <InstagramGrid />
    </div>
  );
}
